import React, { useEffect, useState } from 'react';
import { databaseService } from '../../services/database.service';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import { FaEnvelope, FaEnvelopeOpen, FaTrash, FaPhone, FaUser, FaClock } from 'react-icons/fa';
import { format } from 'date-fns';

const ContactSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const data = await databaseService.getContactSubmissions(100);
      setSubmissions(data);
    } catch (error) {
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);

    // Mark as read if not already read
    if (!submission.read) {
      try {
        await databaseService.markSubmissionAsRead(submission.id, true);
        // Update local state
        setSubmissions(prev =>
          prev.map(s => s.id === submission.id ? { ...s, read: true } : s)
        );
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleToggleRead = async (submission, e) => {
    e.stopPropagation();
    try {
      await databaseService.markSubmissionAsRead(submission.id, !submission.read);
      setSubmissions(prev =>
        prev.map(s => s.id === submission.id ? { ...s, read: !submission.read } : s)
      );
      toast.success(submission.read ? 'Marked as unread' : 'Marked as read');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    try {
      await databaseService.delete(`website/contactSubmissions/${selectedSubmission.id}`);
      toast.success('Submission deleted successfully');
      setShowDeleteDialog(false);
      setShowDetailModal(false);
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const unreadCount = submissions.filter(s => !s.read).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contact Submissions</h1>
            <p className="text-gray-600">
              {unreadCount > 0 ? (
                <span className="font-medium text-primary-600">
                  {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
                </span>
              ) : (
                'All messages read'
              )}
            </p>
          </div>
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {submissions.map((submission) => (
            <Card
              key={submission.id}
              className={`cursor-pointer transition-all hover:shadow-lg ${
                !submission.read ? 'border-l-4 border-primary-600 bg-primary-50/30' : ''
              }`}
              onClick={() => handleViewDetails(submission)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {submission.read ? (
                      <FaEnvelopeOpen className="text-gray-400" />
                    ) : (
                      <FaEnvelope className="text-primary-600" />
                    )}
                    <h3 className={`text-lg font-semibold ${!submission.read ? 'text-gray-900' : 'text-gray-700'}`}>
                      {submission.subject}
                    </h3>
                    {!submission.read && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary-600 text-white rounded-full">
                        New
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                    <div className="flex items-center gap-2">
                      <FaUser className="text-gray-400" />
                      <span>{submission.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaEnvelope className="text-gray-400" />
                      <span>{submission.email}</span>
                    </div>
                    {submission.phone && (
                      <div className="flex items-center gap-2">
                        <FaPhone className="text-gray-400" />
                        <span>{submission.phone}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                    {submission.message}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <FaClock />
                    <span>{format(new Date(submission.submittedAt), 'PPpp')}</span>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={(e) => handleToggleRead(submission, e)}
                    className={`p-2 rounded-lg transition-colors ${
                      submission.read
                        ? 'text-gray-400 hover:text-primary-600 hover:bg-primary-50'
                        : 'text-primary-600 hover:bg-primary-50'
                    }`}
                    title={submission.read ? 'Mark as unread' : 'Mark as read'}
                  >
                    {submission.read ? <FaEnvelopeOpen size={18} /> : <FaEnvelope size={18} />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSubmission(submission);
                      setShowDeleteDialog(true);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <FaTrash size={18} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {submissions.length === 0 && (
          <Card className="text-center py-12">
            <FaEnvelope className="text-gray-400 text-6xl mx-auto mb-4" />
            <p className="text-gray-600">No contact submissions yet</p>
          </Card>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedSubmission(null);
        }}
        title="Contact Submission Details"
        size="lg"
      >
        {selectedSubmission && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <p className="text-lg font-semibold mt-1">{selectedSubmission.subject}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Name</label>
                <p className="mt-1">{selectedSubmission.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1">
                  <a href={`mailto:${selectedSubmission.email}`} className="text-primary-600 hover:underline">
                    {selectedSubmission.email}
                  </a>
                </p>
              </div>
            </div>

            {selectedSubmission.phone && (
              <div>
                <label className="text-sm font-medium text-gray-700">Phone</label>
                <p className="mt-1">
                  <a href={`tel:${selectedSubmission.phone}`} className="text-primary-600 hover:underline">
                    {selectedSubmission.phone}
                  </a>
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <p className="mt-1 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                {selectedSubmission.message}
              </p>
            </div>

            <div className="border-t pt-4">
              <label className="text-sm font-medium text-gray-700">Submitted</label>
              <p className="mt-1 text-sm text-gray-600">
                {format(new Date(selectedSubmission.submittedAt), 'PPpp')}
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowDetailModal(false)}
                fullWidth
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  window.location.href = `mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject}`;
                }}
                fullWidth
              >
                Reply via Email
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Submission"
        message="Are you sure you want to delete this contact submission? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </AdminLayout>
  );
};

export default ContactSubmissions;
